import { useEffect, useState } from 'react'
import { fetchTimeToLead, type TimeToLeadResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'
import { formatDuration } from '../../lib/format'

interface Props { range: { from: number; to: number } }

const fmt = (n: number) => n.toLocaleString('da-DK')

// Buckets where index < 4 are "fast" (under 2 min), 4-6 are "mid", 7+ are "slow"
function toneFor(idx: number): 'fast' | 'mid' | 'slow' {
  if (idx < 4) return 'fast'
  if (idx < 7) return 'mid'
  return 'slow'
}

export function TimeToLeadHistogram({ range }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [data, setData] = useState<TimeToLeadResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchTimeToLead(range, variantParam, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, audienceParam])

  if (error) return <div className="card card-empty">Kunne ikke hente tid-til-lead data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />
  if (data.total === 0) return (
    <div className="card card-empty"><div className="card-title">Tid til lead</div><div className="muted">Ingen leads i perioden</div></div>
  )

  const max = Math.max(1, ...data.buckets.map((b) => b.n))
  // Share of measurable leads under 2 min — 4 first time-buckets are 0-30s,
  // 30-60s, 1-1.5m, 1.5-2m. Use `measurable` (not `total`) so the percentage
  // doesn't get diluted by the "Ukendt" leads with no event timestamp.
  const fastShare = data.measurable > 0
    ? data.buckets.slice(0, 4).reduce((sum, b) => sum + b.n, 0) / data.measurable
    : 0

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Tid til lead</div>
        <div className="card-subtitle">Fra første event til indsendt telefonnummer</div>
      </div>
      <div className="t2l-stats">
        <div className="t2l-stat">
          <div className="stat-label">Median</div>
          <div className="stat-value">{data.median !== null ? formatDuration(data.median) : '—'}</div>
        </div>
        <div className="t2l-stat">
          <div className="stat-label">P90</div>
          <div className="stat-value">{data.p90 !== null ? formatDuration(data.p90) : '—'}</div>
        </div>
        <div className="t2l-stat">
          <div className="stat-label">Hurtige (under 2 min)</div>
          <div className="stat-value">{(fastShare * 100).toFixed(0)}%</div>
        </div>
      </div>
      <div
        className="t2l-bars"
        style={{ gridTemplateColumns: `repeat(${data.buckets.length}, 1fr)` }}
      >
        {data.buckets.map((b, i) => {
          const pct = b.n / max
          const isUnknown = b.label === 'Ukendt'
          const tone = isUnknown ? 'slow' : toneFor(i)
          return (
            <div key={b.label} className="t2l-bar" title={isUnknown ? 'Leads uden registreret event-stream — kan fx skyldes direct API submit eller mistet session-id' : undefined}>
              <div className="t2l-bar-track">
                <div className={`t2l-bar-fill ${tone}`} style={{ height: `${pct * 100}%` }} />
              </div>
              <div className="t2l-bar-count">{fmt(b.n)}</div>
              <div className="t2l-bar-label">{b.label}</div>
            </div>
          )
        })}
      </div>
      {data.unknown > 0 && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {fmt(data.unknown)} af {fmt(data.total)} leads kunne ikke tidsmåles (mangler event-stream).
        </div>
      )}
    </div>
  )
}
